import { useEffect, useRef } from 'react';
import { TranscricaoHistoricoItem } from '../../../../../interfaces/entities/Transcricao/TranscricaoRevisao';
import {
  recuperarTranscricaoDet,
  recuperarTranscricaoHistorico,
  recuperarTranscricaoItem,
} from '../../../../../services/TranscricaoBeneficiarioService';
import { useTranscricao } from '../context';
import { RevisaoStatus } from '../types';

const POLLING_INTERVAL_MS = 10000;

export const possuiStatusPosIA = (historico: TranscricaoHistoricoItem[]): boolean =>
  historico.some((h) => h.tipoStatus?.toUpperCase() === 'POS-IA');

interface UseTranscricaoRevisaoOptions {
  onError?: (title: string, message: string) => void;
}

export const useTranscricaoRevisao = ({ onError }: UseTranscricaoRevisaoOptions = {}) => {
  const { state, dispatch } = useTranscricao();
  const isFetchingRef = useRef(false);
  const revisaoStatusRef = useRef<RevisaoStatus>(state.revisaoStatus);
  const onErrorRef = useRef(onError);

  revisaoStatusRef.current = state.revisaoStatus;
  onErrorRef.current = onError;

  useEffect(() => {
    const idTranscricao = state.idTranscricao;

    if (!idTranscricao) {
      dispatch({ type: 'SET_REVISAO_STATUS', payload: 'erro' });
      onErrorRef.current?.(
        'Transcrição não encontrada',
        'Não foi possível identificar a transcrição. Volte ao passo anterior e tente novamente.',
      );
      return undefined;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const carregarItens = async () => {
      const [itensResultado, detalheResultado] = await Promise.all([
        recuperarTranscricaoItem(idTranscricao),
        recuperarTranscricaoDet(idTranscricao),
      ]);
      if (cancelled) return;

      if (!itensResultado.ok) {
        dispatch({ type: 'SET_REVISAO_STATUS', payload: 'erro' });
        onErrorRef.current?.('Erro ao carregar procedimentos', itensResultado.message);
        return;
      }

      if (!detalheResultado.ok) {
        dispatch({ type: 'SET_REVISAO_STATUS', payload: 'erro' });
        onErrorRef.current?.('Erro ao carregar dados da transcrição', detalheResultado.message);
        return;
      }

      dispatch({ type: 'SET_PROCEDIMENTOS', payload: itensResultado.data });
      dispatch({ type: 'SET_DETALHE_TRANSCRICAO', payload: detalheResultado.data });
      dispatch({ type: 'SET_REVISAO_STATUS', payload: 'pronta' });
    };

    const consultarHistorico = async (isInitial = false) => {
      if (cancelled || isFetchingRef.current) return;
      if (!isInitial && (revisaoStatusRef.current === 'pronta' || revisaoStatusRef.current === 'erro')) {
        return;
      }

      isFetchingRef.current = true;

      if (isInitial) {
        dispatch({ type: 'SET_REVISAO_STATUS', payload: 'carregando' });
      }

      try {
        const historicoResultado = await recuperarTranscricaoHistorico(idTranscricao);
        if (cancelled) return;

        if (!historicoResultado.ok) {
          stopPolling();
          dispatch({ type: 'SET_REVISAO_STATUS', payload: 'erro' });
          onErrorRef.current?.('Erro ao consultar transcrição', historicoResultado.message);
          return;
        }

        if (possuiStatusPosIA(historicoResultado.data)) {
          stopPolling();
          await carregarItens();
          return;
        }

        dispatch({ type: 'SET_REVISAO_STATUS', payload: 'em_andamento' });
      } finally {
        isFetchingRef.current = false;
      }
    };

    void consultarHistorico(true);

    intervalId = setInterval(() => {
      void consultarHistorico();
    }, POLLING_INTERVAL_MS);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [dispatch, state.idTranscricao]);

  return {
    revisaoStatus: state.revisaoStatus,
    procedimentos: state.procedimentos,
    detalheTranscricao: state.detalheTranscricao,
  };
};
